import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import SvgIcon from '@material-ui/core/SvgIcon';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { userHasPermission, userHasRoles } from 'services/Authorization';
import { When } from 'UI/components/atoms/When';
import { getFeatureFlags, nestTernary } from 'UI/utils';

import { NestedMenu } from '../NestedMenu';
import { isActiveRoute } from '../Sidebar.utils';
import { useAccordionSummaryStyles, useSidebarStyles } from '../styles';

import { getItemStyles } from './MenuItem.utils';

const checkIsVisible = ({ featureFlag, roles, permission, showWhen }) => {
  if (showWhen) return showWhen();
  const hasFeaturePermission = !featureFlag || getFeatureFlags().includes(featureFlag);
  const hasRolePermission = !roles || userHasRoles(roles);
  const hasModulePermission = !permission || (permission && userHasPermission(permission));
  return hasRolePermission && hasModulePermission && hasFeaturePermission;
};

export const MenuItem = ({ item, level = 0, expandedItems, onExpand, isSidebarOpen }) => {
  const location = useLocation();

  const classes = useSidebarStyles({ isSidebarOpen, level });
  const accordionSummaryClasses = useAccordionSummaryStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const hasSubItems = item.subItems?.length > 0;
  const visibleSubItems = item.subItems?.filter(checkIsVisible) ?? [];
  const isExpanded = expandedItems[level] === item.route;

  const isParentExpanded = level > 0 && expandedItems[level - 1];

  const isItemActive = !item.subItems && isActiveRoute(item.route, location.pathname);

  const isItemOrChildActive = menuItem => {
    if (menuItem.route === location.pathname) return true;
    if (menuItem.subItems) {
      return menuItem.subItems.some(subItem => isItemOrChildActive(subItem));
    }
    return false;
  };

  const handleCloseNestedMenu = () => setAnchorEl(null);

  const handleMenuItemClick = event => {
    if (!isSidebarOpen && level >= 1 && hasSubItems) {
      setAnchorEl(event.currentTarget);
      return;
    }
    onExpand(item.route, level);
  };

  const { iconClasses, labelClasses } = getItemStyles({
    classes,
    hasSubItems,
    isExpanded,
    isItemActive,
    isItemOrChildActive,
    isSidebarOpen,
    item,
    level
  });
  const itemContent = (
    <>
      <SvgIcon component={item.icon} fontSize="small" className={iconClasses} />
      <Typography
        className={clsx(labelClasses, isSidebarOpen ? classes.labelOpen : classes.labelClose)}
        variant="inherit"
        noWrap
      >
        {item.title}
      </Typography>
    </>
  );

  if (!checkIsVisible(item) || (!isSidebarOpen && level >= 2)) {
    return null;
  }

  const itemPadding = isSidebarOpen
    ? nestTernary(
        level >= 1,
        accordionSummaryClasses.singleItemSubItem,
        accordionSummaryClasses.singleItemBase
      )
    : accordionSummaryClasses.singleItemCollapsed;

  const itemClasses = clsx(
    accordionSummaryClasses.singleItem,
    classes.menuBox,
    isParentExpanded && classes.lightGray,
    itemPadding
  );

  if (!hasSubItems) {
    const to = item.route ?? { pathname: item.url };
    const target = item.url ? '_blank' : undefined;
    return (
      <ListItem disableGutters className={classes.listItem}>
        <Tooltip classes={{ popper: classes.popper }} title={item.title} placement="right" arrow>
          <Link to={to} className="nav-link router-link" target={target}>
            <div className={itemClasses}>{itemContent}</div>
          </Link>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <>
      <Tooltip classes={{ popper: classes.popper }} title={item.title} placement="right" arrow>
        <ListItem
          button
          component="li"
          disableGutters
          id={`panel${item.title}-header`}
          onClick={handleMenuItemClick}
          className={clsx(
            itemClasses,
            isExpanded && accordionSummaryClasses.accordionSummaryExpanded
          )}
        >
          <div className={classes.sectionWithItems}>{itemContent}</div>
          <ExpandMoreIcon
            className={clsx(classes.expandIcon, isExpanded && classes.expandIconOpen)}
          />
        </ListItem>
      </Tooltip>
      <Collapse component="li" in={isExpanded} timeout="auto">
        <List component="ul" disablePadding className={classes.accordionDetails}>
          {visibleSubItems.map(subItem => (
            <MenuItem
              key={subItem.route || subItem.url || subItem.title}
              item={subItem}
              level={level + 1}
              expandedItems={expandedItems}
              onExpand={onExpand}
              isSidebarOpen={isSidebarOpen}
            />
          ))}
          <When condition={!isSidebarOpen && level >= 1 && visibleSubItems.length > 0}>
            <NestedMenu
              items={visibleSubItems}
              onClose={handleCloseNestedMenu}
              anchorEl={anchorEl}
              title={item.title}
            />
          </When>
        </List>
      </Collapse>
    </>
  );
};
